import Product from 'aa/vue/models/Product';
import { sprintf } from 'sprintf-js';
import CouponDiscount from 'aa/vue/models/CouponDiscount'
import Duration from 'aa/vue/models/Duration'

export default class Plan {
    constructor(obj = {}, pageAttrs) {
        //
        // custom properties
        //

        this.addOnCodes = [];
        this.removeBaseTrial = false;
        this.pageAttrs = pageAttrs || {};

        this.couponDiscount = null
        this.couponIsValid = null;
        this.couponRemovesTrial = null;
        /**
         * This is the coupon string provided by xhr when a recurly coupon is used
         * @type {null}
         */
        this.couponString = null;
        this.couponType = null;
        this.currency = null // currency code
        this.applies_for_months = null;

        //
        // PRODUCT.php properties
        //

        /**
         * monthly | annual
         * @type {string}
         */
        this.planType = '';


        /**
         * @type {string}
         */
        this.titleForTracking = '';

        /**
         *
         * @type {string}
         */
        this.price = '';
        this.priceUnit = '';

        /**
         * Prefer using package_code for LCP/LC/CF checks
         * Use tiers numbers for relative order comparison
         * @type {number}
         */
        this.tier = -1;

        /**
         * @type {string}
         */
        this.package_code = '';

        /**
         *
         * @type {string}
         */
        this.trial = '';

        /**
         * REQUIRED TO PURCHASE THE PRODUCT
         * @type {string}
         */
        this.code = '';

        /**
         *
         * @type {string}
         */
        this.recurlyCode = '';

        /**
         *
         * @type {boolean}
         */
        this.isActive = false;

        /**
         *
         * @type {null}
         */
        this.trialInterval = null;

        /**
         *
         * @type {null}
         */
        this.trialUnit = null;

        this.trialEndDate = null;
        this.trialString = null;

        /**
         *
         * @type {string}
         */
        this.planTitle = '';

        /**
         *
         * @type {string}
         */
        this.rawPrice = '';

        this.showtimeAddon = null;

        // override defaults
        if (typeof obj === 'object') {
            let arrKeys = Object.entries(obj);
            for (let idx in arrKeys) {
                let key = arrKeys[idx][0];
                if (this.hasOwnProperty(key)) {
                    /**
                     * pageAttributes passed into Plan object from Store
                     */
                    if (
                        key === 'pageAttrs' &&
                        pageAttrs &&
                        typeof pageAttrs === 'object'
                    ) {
                        continue;
                    } else {
                        this[key] = obj[key];
                    }
                }
            }
        }
    }

    get trialDuration() {
        return new Duration(this.trialInterval, this.trialUnit, true)
    }

    get isCF() {
        return this.package_code === Product.PACKAGE_CODE.CF;
    }

    get isLCP() {
        return this.package_code === Product.PACKAGE_CODE.LCP;
    }

    get isMonthly() {
        return this.planType === Product.TYPE_MONTHLY;
    }

    get isAnnual() {
        return this.planType === Product.TYPE_ANNUAL;
    }

    /**
     * @returns {boolean}
     */
    get bundleShowtime() {
        return this.addOnCodes.includes('SHO');
    }

    setRemoveBaseTrial(bool) {
        this.removeBaseTrial = bool;
        return this;
    }

    setCoupon(config) {
            this.couponIsValid = Boolean(config.couponIsValid)

            if (this.couponIsValid) {
                this.couponDiscount = new CouponDiscount(config)
            } else {
                this.couponDiscount = null
            }

            this.couponRemovesTrial = Boolean(config.couponRemovesTrial)
            this.couponString = config.couponString || null
            this.couponType = this.couponDiscount?.discountType || null
            this.applies_for_months = config?.applies_for_months || null
        }


    getTrialCopy(noCoupon = false, isExSubscriber = false) {
        if (!noCoupon && this.couponString?.length > 0) {
            if (this.couponType === 'percent') {
                if (isExSubscriber) {
                    return null;
                }
                return this.getTrialCopyWithPercentOffCoupon();
            } else {
                return this.getTrialCopyWithCoupon();
            }
        } else {
            return this.getTrialCopyWithoutCoupon();
        }
    }

    getTrialCopyWithPercentOffCoupon() {
        if (this.hasValidCoupon()) {
            return this.getTrialCopyWithoutCoupon();
        }
        let cs = this.couponString;
        if (this.removeBaseTrial) {
            return cs;
        } else {
            return this.getTrialCopyWithoutCoupon() + ' & ' + cs;
        }
    }

    isCouponValidAndPercent() {
        return this.couponIsValid === true && this.couponType === 'percent';
    }

    hasValidCoupon() {
        return this.couponIsValid === true && this.couponString !== null;
    }

    getAdjustedPromoRateString() {
        if (this.planType === Product.TYPE_ANNUAL) {
            return this.getPromoStringByYear();
        } else if (this.planType === Product.TYPE_MONTHLY && typeof this.applies_for_months === 'number') {
            return this.getPromoStringByMonth();
        }
    }

    getPromoStringByMonth() {
        const rate = this.getAdjustedPromotionalRate() ?? '';
        const durationPromo = this.applies_for_months;
        let month = 'month';
        let format = '$%s for first %s %s';

        if (durationPromo > 1) {
            month = 'months';
        }

        let promoStr;
        try {
            promoStr = sprintf(format, rate, durationPromo, month);
        } catch(e) {
            promoStr = '';
        }

        return promoStr;
    }

    getPromoStringByYear() {
        let rate = this.getAdjustedPromotionalRate() ?? '';
        let format = '$%s for first year';

        let promoStr;
        try {
            promoStr = sprintf(format, rate);
        } catch(e) {
            promoStr = '';
        }

        return promoStr;
    }
    
    getRawPrice() {
        return Number(this.rawPrice);
    }

    getAdjustedPromotionalRate() {
        if (this.couponDiscount?.priceSubtotal && this.couponDiscount?.discountType === 'percent') {
            return this.couponDiscount.priceSubtotal;
        } else {
            return null;
        }
    }

    getRetailPrice() {
        return "$"+ Number.parseFloat(this.getRawPrice()).toFixed(2);
    }

    planRetailPrice() {
        return this.getAdjustedPromoRateString() ? this.getRetailPrice() : '';
    }

    getTrialCopyWithCoupon() {
        return this.pageAttrs.aa_pap_coupon_prefix + ' ' + this.couponString;
    }
    
    getTrialCopyWithoutCoupon(
        format = '%s %s FREE',
        plural = true,
        ucFirst = true
    ) {
        let copyFormat = format ? format : `%s %s ${getLocale('free')}`;
        // override default format from page attrs
        if (this.pageAttrs.aa_pap_trial_copy) {
            copyFormat = this.pageAttrs.aa_pap_trial_copy;
        }


        const durationCopy = this.trialDuration.toCopyParts(plural, ucFirst)
        let ret;

        try {
            ret = sprintf(copyFormat, durationCopy.amount, durationCopy.unit);
        } catch (e) {
            ret = '';
        }

        return ret;
    }

    /**
     * @returns {string|null}
     */
    annualPlanMonthlyPriceWithUnit() {
        if (this.planType !== Product.TYPE_ANNUAL) {
            return null;
        }

        if (!this.rawPrice) {
            return null;
        }

        let price = Number.parseFloat(this.rawPrice);

        price = (price / 12).toFixed(2);

        return `$${price}/month`;
    }

    /**
     * @param plan
     * @returns {boolean}
     */
    basePackageMatches(plan) {
        return this.package_code === plan.package_code;
    }

    /**
     * @param plan
     * @returns {boolean}
     */
    basePackageAndPlanTypeMatches(plan) {
        return this.basePackageMatches(plan) && this.planType === plan.planType;
    }

    /**
     * @param plan
     * @returns {boolean}
     */
    allPackagesMatch(plan) {
        return (
            this.basePackageMatches(plan) &&
            this.addOnCodes.join('|') === plan.addOnCodes.join('|')
        );
    }
}
